import { Component, OnInit, OnDestroy, Input, ViewEncapsulation, Output, EventEmitter } from '@angular/core';
import { IdService } from '../../shared/services/id.service';
import { TIconType, TIconSize, TIconRotation, TIconAnimation, TIconCircleSize } from './icon.types';

@Component({
	selector: 'rbc-icon',
	templateUrl: './icon.component.html',
	styleUrls: ['./icon.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class IconComponent implements OnInit, OnDestroy {
	// Private Members
	block = 'rbc-icon';

	// Input Parameters
	@Input() id: string;
	@Input() type: TIconType = 'regular';
	@Input() icon: string;
	@Input() size: TIconSize = '';
	@Input() fixedWidth: boolean = false;
	@Input() rotation: TIconRotation = '';
	@Input() animation: TIconAnimation = '';
	@Input() animateOn: string;
	@Input() circle: boolean = false;
	@Input() circleBorder: boolean = false;
	@Input() circleColor: string;
	@Input() circleSize: TIconCircleSize = '';
	@Input() title: boolean;
	@Input() label: string;
	@Input() role: string;

	@Output() keyUp = new EventEmitter();
	@Output() click = new EventEmitter();

	constructor(private idService: IdService) {}

	ngOnDestroy(): void {
		this.idService.remove(this.id);
	}

	ngOnInit() {
		if (!this.id) {
			this.id = this.idService.generate();
		}
		this.validateInputParameters();
	}

	validateInputParameters() {
		// icon
		if (!this.icon) {
			throw new Error('"icon" property is required');
		}
	}

	calculateWrapperClasses() {
		const classes = {};
		classes['icon-wrapper'] = true;
		classes[`b-${this.circleColor}`] = this.circleColor && this.circle;
		classes[`icon-circle-${this.circleSize}`] = this.circleSize;
		classes['icon-circle'] = this.circle;
		classes['icon-circle-border'] = this.circleBorder && this.circle;
		classes[`fa-${this.size}`] = this.size;

		return classes;
	}

	calculateIconClasses() {
		const classes = {};
		classes['icon'] = true;
		classes[`${this.iconType()}`] = this.iconType();
		classes[`fa-${this.icon}`] = this.icon;
		classes[`fa-${this.rotation}`] = this.rotation;
		classes[`fa-${this.animation}`] = this.animation;
		classes['fa-fw'] = this.fixedWidth;
		classes['icon-animate'] = this.animateOn;

		return classes;
	}

	labelGen() {
		return this.label || this.icon.replace(/-/g, ' ');
	}

	onKeyUp($event) {
		this.keyUp.emit($event);
	}

	onClick($event) {
		this.click.emit($event);
	}

	iconType() {
		const brandArray = [
			'500px',
			'accessible-icon',
			'accusoft',
			'adn',
			'adversal',
			'affiliatetheme',
			'algolia',
			'amazon',
			'amazon-pay',
			'amilia',
			'android',
			'angellist',
			'angrycreative',
			'angular',
			'app-store',
			'app-store-ios',
			'apper',
			'apple',
			'apple-pay',
			'asymmetrik',
			'audible',
			'autoprefixer',
			'avianex',
			'aviato',
			'aws',
			'bandcamp',
			'behance',
			'behance-square',
			'bimobject',
			'bitbucket',
			'bitcoin',
			'bity',
			'black-tie',
			'blackberry',
			'blogger',
			'blogger-b',
			'bluetooth',
			'bluetooth-b',
			'btc',
			'buromobelexperte',
			'cc-amazon-pay',
			'cc-amex',
			'cc-apple-pay',
			'cc-diners-club',
			'cc-discover',
			'cc-jcb',
			'cc-mastercard',
			'cc-paypal',
			'cc-stripe',
			'cc-visa',
			'centercode',
			'chrome',
			'cloudscale',
			'cloudsmith',
			'cloudversify',
			'codepen',
			'codiepie',
			'connectdevelop',
			'contao',
			'cpanel',
			'creative-commons',
			'creative-commons-by',
			'creative-commons-nc',
			'creative-commons-nc-eu',
			'creative-commons-nc-jp',
			'creative-commons-nd',
			'creative-commons-pd',
			'creative-commons-pd-alt',
			'creative-commons-remix',
			'creative-commons-sa',
			'creative-commons-sampling',
			'creative-commons-sampling-plus',
			'creative-commons-share',
			'creative-commons-zero',
			'css3',
			'css3-alt',
			'cuttlefish',
			'd-and-d',
			'dashcube',
			'delicious',
			'deploydog',
			'deskpro',
			'deviantart',
			'digg',
			'digital-ocean',
			'discord',
			'discourse',
			'dochub',
			'docker',
			'draft2digital',
			'dribbble',
			'dribbble-square',
			'dropbox',
			'drupal',
			'dyalog',
			'earlybirds',
			'ebay',
			'edge',
			'elementor',
			'ember',
			'empire',
			'envira',
			'erlang',
			'ethereum',
			'etsy',
			'expeditedssl',
			'facebook',
			'facebook-f',
			'facebook-messenger',
			'facebook-square',
			'firefox',
			'first-order',
			'first-order-alt',
			'firstdraft',
			'flickr',
			'flipboard',
			'fly',
			'font-awesome',
			'font-awesome-alt',
			'font-awesome-flag',
			'font-awesome-logo-full',
			'fonticons',
			'fonticons-fi',
			'fort-awesome',
			'fort-awesome-alt',
			'forumbee',
			'foursquare',
			'free-code-camp',
			'freebsd',
			'fulcrum',
			'galactic-republic',
			'galactic-senate',
			'get-pocket',
			'gg',
			'gg-circle',
			'git',
			'git-square',
			'github',
			'github-alt',
			'github-square',
			'gitkraken',
			'gitlab',
			'gitter',
			'glide',
			'glide-g',
			'gofore',
			'goodreads',
			'goodreads-g',
			'google',
			'google-drive',
			'google-play',
			'google-plus',
			'google-plus-g',
			'google-plus-square',
			'google-wallet',
			'gratipay',
			'grav',
			'gripfire',
			'grunt',
			'gulp',
			'hacker-news',
			'hacker-news-square',
			'hips',
			'hire-a-helper',
			'hooli',
			'hornbill',
			'hotjar',
			'houzz',
			'html5',
			'hubspot',
			'imdb',
			'instagram',
			'internet-explorer',
			'ioxhost',
			'itunes',
			'itunes-note',
			'java',
			'jedi-order',
			'jenkins',
			'joget',
			'joomla',
			'js',
			'js-square',
			'jsfiddle',
			'keybase',
			'keycdn',
			'kickstarter',
			'kickstarter-k',
			'korvue',
			'laravel',
			'lastfm',
			'lastfm-square',
			'leanpub',
			'less',
			'line',
			'linkedin',
			'linkedin-in',
			'linode',
			'linux',
			'lyft',
			'magento',
			'mailchimp',
			'mandalorian',
			'mastodon',
			'maxcdn',
			'medapps',
			'medium',
			'medium-m',
			'medrt',
			'meetup',
			'megaport',
			'microsoft',
			'mix',
			'mixcloud',
			'mizuni',
			'modx',
			'monero',
			'napster',
			'nimblr',
			'nintendo-switch',
			'node',
			'node-js',
			'npm',
			'ns8',
			'nutritionix',
			'odnoklassniki',
			'odnoklassniki-square',
			'old-republic',
			'opencart',
			'openid',
			'opera',
			'optin-monster',
			'osi',
			'page4',
			'pagelines',
			'palfed',
			'patreon',
			'paypal',
			'periscope',
			'phabricator',
			'phoenix-framework',
			'phoenix-squadron',
			'php',
			'pied-piper',
			'pied-piper-alt',
			'pied-piper-hat',
			'pied-piper-pp',
			'pinterest',
			'pinterest-p',
			'pinterest-square',
			'playstation',
			'product-hunt',
			'pushed',
			'python',
			'qq',
			'quinscape',
			'quora',
			'r-project',
			'ravelry',
			'react',
			'readme',
			'rebel',
			'red-river',
			'reddit',
			'reddit-alien',
			'reddit-square',
			'rendact',
			'renren',
			'replyd',
			'researchgate',
			'resolving',
			'rev',
			'rocketchat',
			'rockrms',
			'safari',
			'sass',
			'schlix',
			'scribd',
			'searchengin',
			'sellcast',
			'sellsy',
			'servicestack',
			'shirtsinbulk',
			'shopware',
			'simplybuilt',
			'sistrix',
			'sith',
			'skyatlas',
			'skype',
			'slack',
			'slack-hash',
			'slideshare',
			'snapchat',
			'snapchat-ghost',
			'snapchat-square',
			'soundcloud',
			'speakap',
			'spotify',
			'squarespace',
			'stack-exchange',
			'stack-overflow',
			'staylinked',
			'steam',
			'steam-square',
			'steam-symbol',
			'sticker-mule',
			'strava',
			'stripe',
			'stripe-s',
			'studiovinari',
			'stumbleupon',
			'stumbleupon-circle',
			'superpowers',
			'supple',
			'teamspeak',
			'telegram',
			'telegram-plane',
			'tencent-weibo',
			'themeco',
			'themeisle',
			'trade-federation',
			'trello',
			'tripadvisor',
			'tumblr',
			'tumblr-square',
			'twitch',
			'twitter',
			'twitter-square',
			'typo3',
			'uber',
			'uikit',
			'uniregistry',
			'untappd',
			'usb',
			'ussunnah',
			'vaadin',
			'viacoin',
			'viadeo',
			'viadeo-square',
			'viber',
			'vimeo',
			'vimeo-square',
			'vimeo-v',
			'vine',
			'vk',
			'vnv',
			'vuejs',
			'weebly',
			'weibo',
			'weixin',
			'whatsapp',
			'whatsapp-square',
			'whmcs',
			'wikipedia-w',
			'windows',
			'wix',
			'wolf-pack-battalion',
			'wordpress',
			'wordpress-simple',
			'wpbeginner',
			'wpexplorer',
			'wpforms',
			'xbox',
			'xing',
			'xing-square',
			'y-combinator',
			'yahoo',
			'yandex',
			'yandex-international',
			'yelp',
			'yoast',
			'youtube',
			'youtube-square'
		];
		if (brandArray.indexOf(this.icon) !== -1) {
			return 'fab';
		} else {
			return 'fa' + this.type.substring(0, 1);
		}
	}
}
